import { useMemo, useState } from 'react'
import { isAxiosError } from 'axios'
import { Archive, Building2, ChevronDown, ChevronRight, Plus, Search, UserCog } from 'lucide-react'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SkeletonRows } from '@/components/ui/skeleton'

import { useUsers, type AdminUser } from './api'
import {
  useArchiveOrgNode,
  useCreateOrgNode,
  useOrgTree,
  usePlaceAgent,
  useUpdateOrgNode,
  type NodeType,
  type OrgNode,
} from './orgApi'

/**
 * Per-tenant org-structure admin. Closes the gap that left every agent's trip
 * submission failing with "No mapped manager found" — until this page shipped
 * there was no surface for binding agents to a BRANCH with a manager.
 *
 * <p>The tree mirrors {@code org_nodes}: REGION → AREA → BRANCH → TEAM → AGENT.
 * Non-AGENT nodes can carry a {@code manager_user_id} — the manager-resolver
 * walks upward from the AGENT and returns the first non-null match.</p>
 */
export function OrgAdminPage() {
  const tree = useOrgTree()
  const users = useUsers()

  const usersById = useMemo(() => {
    const m = new Map<string, AdminUser>()
    for (const u of users.data ?? []) m.set(u.userId, u)
    return m
  }, [users.data])

  const childrenByParent = useMemo(() => {
    const m = new Map<string | null, OrgNode[]>()
    for (const n of tree.data ?? []) {
      const k = n.parentId
      if (!m.has(k)) m.set(k, [])
      m.get(k)!.push(n)
    }
    for (const arr of m.values()) arr.sort((a, b) => a.lft - b.lft)
    return m
  }, [tree.data])

  const [showRoot, setShowRoot] = useState(false)
  const [q, setQ] = useState('')

  /**
   * Node ids to render while searching: every match, plus all of its
   * ancestors.
   *
   * A tree cannot simply drop non-matching rows — a matching AGENT sitting
   * four levels down would be orphaned, and you would lose the very thing the
   * hierarchy is there to show. Keeping the ancestors renders the match in
   * place, as a path.
   *
   * Null means "not searching", which is different from "searching and nothing
   * matched" (an empty set).
   */
  const visibleIds = useMemo(() => {
    const norm = q.trim().toLowerCase()
    if (!norm) return null
    const all = tree.data ?? []
    const byId = new Map(all.map((n) => [n.nodeId, n]))
    const keep = new Set<string>()
    for (const n of all) {
      const manager = n.managerUserId ? usersById.get(n.managerUserId) : undefined
      const agent = n.agentUserId ? usersById.get(n.agentUserId) : undefined
      const hay = [
        n.code, n.name, n.nodeType,
        manager?.empId, manager?.fullName,
        agent?.empId, agent?.fullName,
      ]
      if (!hay.some((v) => v?.toLowerCase().includes(norm))) continue
      // walk up to the root so the match keeps its context
      let cur: OrgNode | undefined = n
      while (cur && !keep.has(cur.nodeId)) {
        keep.add(cur.nodeId)
        cur = cur.parentId ? byId.get(cur.parentId) : undefined
      }
    }
    return keep
  }, [q, tree.data, usersById])

  const allRoots = childrenByParent.get(null) ?? []
  const roots = visibleIds ? allRoots.filter((n) => visibleIds.has(n.nodeId)) : allRoots

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-2xs font-semibold uppercase tracking-wider text-navy-600">
            Tenant administration
          </p>
          <h1 className="mt-1 flex items-center gap-2 text-2xl font-semibold tracking-tight text-navy-900">
            <Building2 className="h-5 w-5" /> Org structure
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Region → Area → Branch → Team → Agent. Visit-request approval walks
            this tree upward to find each agent's mapped manager.
          </p>
        </div>
        <Button size="sm" onClick={() => setShowRoot((v) => !v)}>
          <Plus className="h-3.5 w-3.5" />
          Add root
        </Button>
      </div>

      {showRoot && (
        <NewNodeForm
          parent={null}
          users={users.data ?? []}
          onClose={() => setShowRoot(false)}
        />
      )}

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle>Tree</CardTitle>
              <CardDescription>
                {!tree.data
                  ? '—'
                  : visibleIds
                    ? `${visibleIds.size} nodes match (ancestors shown for context)`
                    : `${tree.data.filter((n) => n.status === 'ACTIVE').length} active nodes`}
              </CardDescription>
            </div>
            <div className="relative w-full max-w-xs">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-navy-500" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Code, name, manager or agent…"
                className="h-8 pl-8 text-xs"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-0 pb-0">
          {tree.isPending && <SkeletonRows rows={4} cols={3} />}
          {tree.isError && (
            <div className="px-5 pb-4 text-sm text-negative">
              Couldn't load the tree — ORG_HIERARCHY_VIEW is required.
            </div>
          )}
          {tree.data && roots.length === 0 && (
            <div className="px-5 py-10 text-center">
              <p className="text-sm font-medium text-navy-900">
                {visibleIds ? 'No matching nodes' : 'No org nodes yet'}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {visibleIds
                  ? 'Search matches node code, name, type, and the manager or agent on it.'
                  : 'Start with a Branch — add one with "Add root" above.'}
              </p>
            </div>
          )}
          {tree.data && roots.length > 0 && (
            <ul className="divide-y divide-slate-200">
              {roots.map((n) => (
                <NodeRow
                  key={n.nodeId}
                  node={n}
                  depth={0}
                  childrenByParent={childrenByParent}
                  usersById={usersById}
                  users={users.data ?? []}
                  visibleIds={visibleIds}
                />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tree node
// ---------------------------------------------------------------------------

function NodeRow({
  node,
  depth,
  childrenByParent,
  usersById,
  users,
  visibleIds,
}: {
  node: OrgNode
  depth: number
  childrenByParent: Map<string | null, OrgNode[]>
  usersById: Map<string, AdminUser>
  users: AdminUser[]
  /** Null when not searching; otherwise the matches plus their ancestors. */
  visibleIds: Set<string> | null
}) {
  // Collapsed branches would hide the very rows the search found, so while a
  // search is running every surviving node is open.
  const [open, setOpen] = useState(depth < 1)
  const expanded = visibleIds ? true : open
  const [showAddChild, setShowAddChild] = useState(false)
  const [showAddAgent, setShowAddAgent] = useState(false)
  const [editing, setEditing] = useState(false)

  const archive = useArchiveOrgNode()
  const update = useUpdateOrgNode()
  const allChildren = childrenByParent.get(node.nodeId) ?? []
  const children = visibleIds
    ? allChildren.filter((c) => visibleIds.has(c.nodeId))
    : allChildren
  const isArchived = node.status === 'ARCHIVED'
  const canHaveChildren = node.nodeType !== 'AGENT'
  const isAgent = node.nodeType === 'AGENT'
  const managerLabel = node.managerUserId ? userLabel(usersById.get(node.managerUserId), node.managerUserId) : '—'
  const agentLabel = node.agentUserId ? userLabel(usersById.get(node.agentUserId), node.agentUserId) : null

  function onArchive() {
    if (archive.isPending) return
    const ok = window.confirm(
      `Archive ${node.code}? It drops out of manager resolution immediately. The action is logged.`,
    )
    if (ok) archive.mutate(node.nodeId)
  }

  return (
    <li className={isArchived ? 'opacity-50' : ''}>
      <div className="flex items-center gap-2 px-5 py-2" style={{ paddingLeft: 20 + depth * 16 }}>
        {children.length > 0 ? (
          <button
            type="button"
            className="text-navy-700"
            onClick={() => setOpen((v) => !v)}
            aria-label={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
        ) : (
          <span className="inline-block h-4 w-4" />
        )}
        <Badge tone={isAgent ? 'navy' : 'slate'}>{node.nodeType}</Badge>
        <span className="font-mono text-2xs text-navy-700">{node.code}</span>
        <span className="text-sm text-navy-900">{node.name}</span>
        {isAgent && agentLabel && (
          <span className="text-2xs text-muted-foreground">· {agentLabel}</span>
        )}
        {!isAgent && (
          <span className="text-2xs text-muted-foreground">
            · manager: {managerLabel}
          </span>
        )}
        {isArchived && <Badge tone="slate">ARCHIVED</Badge>}
        <div className="ml-auto flex items-center gap-1">
          {canHaveChildren && !isArchived && (
            <>
              <Button variant="outline" size="sm" onClick={() => setShowAddChild((v) => !v)}>
                <Plus className="h-3 w-3" /> Sub-node
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowAddAgent((v) => !v)}>
                <UserCog className="h-3 w-3" /> Agent
              </Button>
              <Button variant="outline" size="sm" onClick={() => setEditing((v) => !v)}>
                Edit
              </Button>
            </>
          )}
          {!isArchived && (
            <Button variant="outline" size="sm" onClick={onArchive} disabled={archive.isPending}>
              <Archive className="h-3 w-3" /> Archive
            </Button>
          )}
        </div>
      </div>

      {editing && !isAgent && (
        <EditNodeForm
          node={node}
          users={users}
          onClose={() => setEditing(false)}
          onSubmit={(body) => {
            update.mutate({ nodeId: node.nodeId, ...body }, { onSuccess: () => setEditing(false) })
          }}
          isPending={update.isPending}
        />
      )}
      {showAddChild && (
        <NewNodeForm
          parent={node}
          users={users}
          onClose={() => setShowAddChild(false)}
        />
      )}
      {showAddAgent && (
        <PlaceAgentForm
          parent={node}
          users={users}
          onClose={() => setShowAddAgent(false)}
        />
      )}

      {expanded && children.length > 0 && (
        <ul className="border-t border-slate-100">
          {children.map((c) => (
            <NodeRow
              key={c.nodeId}
              node={c}
              depth={depth + 1}
              childrenByParent={childrenByParent}
              usersById={usersById}
              users={users}
              visibleIds={visibleIds}
            />
          ))}
        </ul>
      )}
    </li>
  )
}

// ---------------------------------------------------------------------------
// Forms
// ---------------------------------------------------------------------------

function NewNodeForm({
  parent,
  users,
  onClose,
}: {
  parent: OrgNode | null
  users: AdminUser[]
  onClose: () => void
}) {
  const create = useCreateOrgNode()
  const allowedTypes = nextLevelTypes(parent?.nodeType ?? null)
  const [nodeType, setNodeType] = useState<NodeType>(allowedTypes[0] as NodeType)
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [managerUserId, setManagerUserId] = useState<string>('')

  function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!code || !name) return
    create.mutate(
      {
        parentId: parent?.nodeId ?? null,
        nodeType: nodeType as Exclude<NodeType, 'AGENT'>,
        code: code.trim(),
        name: name.trim(),
        managerUserId: managerUserId || null,
      },
      {
        onSuccess: () => {
          setCode('')
          setName('')
          setManagerUserId('')
          onClose()
        },
      },
    )
  }

  return (
    <div className="border-y border-slate-200 bg-navy-50/40 px-5 py-3">
      <p className="mb-3 text-2xs font-semibold uppercase tracking-wider text-navy-700">
        {parent ? `New sub-node under ${parent.code}` : 'New root node'}
      </p>
      <form onSubmit={onSubmit} className="grid gap-3 sm:grid-cols-4">
        <div>
          <Label>Type</Label>
          <select
            className="mt-1 block w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-sm"
            value={nodeType}
            onChange={(e) => setNodeType(e.target.value as NodeType)}
          >
            {allowedTypes.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        <div>
          <Label>Code</Label>
          <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="BR-MUM" />
        </div>
        <div>
          <Label>Name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Mumbai" />
        </div>
        <div>
          <Label>Manager (optional)</Label>
          <select
            className="mt-1 block w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-sm"
            value={managerUserId}
            onChange={(e) => setManagerUserId(e.target.value)}
          >
            <option value="">—</option>
            {users
              .filter((u) => u.status === 'ACTIVE')
              .map((u) => (
                <option key={u.userId} value={u.userId}>
                  {u.empId} · {u.fullName}
                </option>
              ))}
          </select>
        </div>
        <FormFooter
          onClose={onClose}
          error={mutationError(create.error, create.isError)}
          isPending={create.isPending}
          disabled={!code || !name}
          submitLabel="Create"
        />
      </form>
    </div>
  )
}

function PlaceAgentForm({
  parent,
  users,
  onClose,
}: {
  parent: OrgNode
  users: AdminUser[]
  onClose: () => void
}) {
  const place = usePlaceAgent()
  const [agentUserId, setAgentUserId] = useState<string>('')
  const [code, setCode] = useState('')
  const [name, setName] = useState('')

  function onSelectAgent(userId: string) {
    setAgentUserId(userId)
    const u = users.find((x) => x.userId === userId)
    if (u && !code) setCode(`AG-${u.empId}`)
    if (u && !name) setName(u.fullName)
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!agentUserId || !code || !name) return
    place.mutate(
      { parentId: parent.nodeId, agentUserId, code: code.trim(), name: name.trim() },
      {
        onSuccess: () => {
          setAgentUserId(''); setCode(''); setName('')
          onClose()
        },
      },
    )
  }

  return (
    <div className="border-y border-slate-200 bg-navy-50/40 px-5 py-3">
      <p className="mb-3 text-2xs font-semibold uppercase tracking-wider text-navy-700">
        Place an agent under {parent.code}
      </p>
      <form onSubmit={onSubmit} className="grid gap-3 sm:grid-cols-4">
        <div className="sm:col-span-2">
          <Label>Agent</Label>
          <select
            className="mt-1 block w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-sm"
            value={agentUserId}
            onChange={(e) => onSelectAgent(e.target.value)}
          >
            <option value="">Pick a user…</option>
            {users
              .filter((u) => u.status === 'ACTIVE')
              .map((u) => (
                <option key={u.userId} value={u.userId}>
                  {u.empId} · {u.fullName} · {u.roles.join(', ') || 'no roles'}
                </option>
              ))}
          </select>
        </div>
        <div>
          <Label>Code</Label>
          <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="AG-AG01" />
        </div>
        <div>
          <Label>Display name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <FormFooter
          onClose={onClose}
          error={mutationError(place.error, place.isError)}
          isPending={place.isPending}
          disabled={!agentUserId || !code || !name}
          submitLabel="Place agent"
        />
      </form>
    </div>
  )
}

function EditNodeForm({
  node,
  users,
  onClose,
  onSubmit,
  isPending,
}: {
  node: OrgNode
  users: AdminUser[]
  onClose: () => void
  onSubmit: (body: { name?: string; managerUserId?: string | null; clearManager?: boolean }) => void
  isPending: boolean
}) {
  const [name, setName] = useState(node.name)
  const [managerUserId, setManagerUserId] = useState<string>(node.managerUserId ?? '')

  function submit(e: React.FormEvent) {
    e.preventDefault()
    const body: { name?: string; managerUserId?: string | null; clearManager?: boolean } = {}
    if (name && name !== node.name) body.name = name.trim()
    if (managerUserId && managerUserId !== node.managerUserId) body.managerUserId = managerUserId
    else if (!managerUserId && node.managerUserId) body.clearManager = true
    onSubmit(body)
  }

  return (
    <div className="border-y border-slate-200 bg-navy-50/40 px-5 py-3">
      <p className="mb-3 text-2xs font-semibold uppercase tracking-wider text-navy-700">
        Edit {node.code}
      </p>
      <form onSubmit={submit} className="grid gap-3 sm:grid-cols-3">
        <div>
          <Label>Name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="sm:col-span-2">
          <Label>Manager</Label>
          <select
            className="mt-1 block w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-sm"
            value={managerUserId}
            onChange={(e) => setManagerUserId(e.target.value)}
          >
            <option value="">— (no manager — inherit from ancestor)</option>
            {users
              .filter((u) => u.status === 'ACTIVE')
              .map((u) => (
                <option key={u.userId} value={u.userId}>
                  {u.empId} · {u.fullName}
                </option>
              ))}
          </select>
        </div>
        <FormFooter onClose={onClose} error={null} isPending={isPending} disabled={false} submitLabel="Save" />
      </form>
    </div>
  )
}

function FormFooter({
  onClose,
  error,
  isPending,
  disabled,
  submitLabel,
}: {
  onClose: () => void
  error: string | null
  isPending: boolean
  disabled: boolean
  submitLabel: string
}) {
  return (
    <div className="sm:col-span-4 flex flex-col gap-2">
      {error && (
        <Alert variant="destructive">
          <AlertTitle>Couldn't save</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <div className="flex items-center justify-end gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={disabled || isPending}>
          {isPending ? 'Saving…' : submitLabel}
        </Button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function nextLevelTypes(parentType: NodeType | null): Exclude<NodeType, 'AGENT'>[] {
  // Tenants can skip levels — the BRD allows REGION → BRANCH directly when there
  // are no AREAs. The constraint is only that AGENT must hang off a non-AGENT node.
  switch (parentType) {
    case null:
      return ['REGION', 'AREA', 'BRANCH']
    case 'REGION':
      return ['AREA', 'BRANCH']
    case 'AREA':
      return ['BRANCH']
    case 'BRANCH':
      return ['TEAM']
    case 'TEAM':
      return ['TEAM']
    default:
      return ['BRANCH']
  }
}

function userLabel(u: AdminUser | undefined, fallbackId: string) {
  if (!u) return fallbackId.slice(0, 8)
  return `${u.empId} · ${u.fullName}`
}

function mutationError(err: unknown, isError: boolean): string | null {
  if (!isError) return null
  if (isAxiosError(err)) {
    if (err.response?.status === 403) return 'ORG_HIERARCHY_MANAGE is required.'
    if (err.response?.status === 409) return err.response?.data?.detail ?? 'Conflict — archive children first.'
    return err.response?.data?.detail ?? err.response?.data?.message ?? 'Request failed.'
  }
  return 'Request failed.'
}
